/**
 * React Query Hook for fetching tutor details
 */

import { useQuery } from '@tanstack/react-query';
import { getTutorById } from '@/api/services/tutorsApi';
import { useAuthContext } from '@/contexts';
import { getViewerIdentity, tutorQueryKeys } from './queryKeys';

/**
 * Query hook for fetching a specific tutor by ID
 * Uses TanStack Query with caching strategy
 * Cache key: ['tutor', id]
 */
export const useTutorDetail = (id: string | undefined, source?: string) => {
  const { firebaseUser, profile } = useAuthContext();
  const viewerIdentity = getViewerIdentity(firebaseUser, profile);

  return useQuery({
    queryKey: tutorQueryKeys.detail(viewerIdentity, id),
    queryFn: async ({ signal }) => {
      if (!id) {
        throw new Error('Tutor ID is required');
      }
      return getTutorById(id, source, signal);
    },
    enabled: !!id, // Only run query if id is provided
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
};
